/**
 * Experts Tab — expert & company challenges with category filter.
 * Spec: challenges .txt § 5.2.
 */

import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { CategoryChips } from './category-chips'
import { BrowseChallengeCard } from './browse-challenge-card'
import { useBrowseChallenges } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'

export const ExpertsTab = memo(function ExpertsTab(): React.ReactElement {
  const { t } = useTranslation()
  const [category, setCategory] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  const { data: challenges, isLoading } = useBrowseChallenges({
    category,
    types: ['public'],
    sort: 'popular',
    page,
  })

  const handleLoadMore = useCallback((): void => {
    setPage((p) => p + 1)
  }, [])

  return (
    <div className="space-y-4">
      {/* Category chips */}
      <CategoryChips
        selected={category}
        onSelect={(id) => { setCategory(id); setPage(0) }}
      />

      {/* Challenge list */}
      {isLoading && page === 0 ? (
        <div className="space-y-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[100px] rounded-2xl" />
          ))}
        </div>
      ) : (challenges?.length ?? 0) === 0 ? (
        <EmptyState
          icon={null}
          title={t('challenges.browse.noResults')}
          description={t('challenges.browse.noResultsDesc')}
        />
      ) : (
        <>
          <div className="space-y-2.5">
            {(challenges ?? []).map((c) => (
              <BrowseChallengeCard key={c.id} challenge={c} />
            ))}
          </div>

          {(challenges?.length ?? 0) >= 20 && (
            <button
              onClick={handleLoadMore}
              className="w-full mt-2 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-text-hint font-medium hover:bg-white/[0.05] transition-colors tap-highlight-transparent"
            >
              {t('challenges.browse.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  )
})
